import React, { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Button } from "./ui/button";
import { Phone, Mail, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { faqMaPrimeAdapt } from "../utils/faqMaPrimeAdapt";
import ContactModal from "./ContactModal";

// Sur l'accueil on n'affiche qu'une sélection : la liste complète vit sur
// /maprimeadapt, qui est la page de référence sur le dispositif.
const NB_SUR_ACCUEIL = 10;

const FAQ = ({ questions = faqMaPrimeAdapt.slice(0, NB_SUR_ACCUEIL) }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const resteDesQuestions = faqMaPrimeAdapt.length - questions.length;

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Questions fréquentes
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Tout ce que vous devez savoir sur MaPrimeAdapt' et notre accompagnement AMO en Île-de-France
            </p>
          </div>

          <div className="mb-12">
            <Accordion type="single" collapsible className="space-y-4">
              {questions.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={`item-${item.id}`}
                  className="border border-gray-300 rounded-lg px-6 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-orange-700 py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {resteDesQuestions > 0 && (
              <div className="text-center mt-8">
                <Link
                  to="/maprimeadapt"
                  className="inline-flex items-center min-h-[44px] text-orange-700 font-medium hover:text-orange-800 underline"
                >
                  Voir les {faqMaPrimeAdapt.length} questions sur MaPrimeAdapt'
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl p-8 lg:p-12 text-center text-white">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Prêt à adapter votre logement ?
            </h3>
            <p className="text-xl mb-8">
              Testez votre éligibilité MaPrimeAdapt' en 3 minutes ou contactez nos conseillers
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto">
              <Link to="/simulateur" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="bg-white text-orange-700 hover:bg-gray-100 w-full sm:w-auto group shadow-lg"
                >
                  Simulateur gratuit
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-orange-700 w-full sm:w-auto"
                onClick={() => window.location.href = 'tel:0939035444'}
              >
                <Phone className="mr-2 h-5 w-5" />
                09 39 03 54 44
              </Button>
            </div>

            <div className="mt-8 pt-8 border-t border-orange-500 opacity-75">
              <p className="text-sm">
                <Mail className="inline h-4 w-4 mr-2" />
                contact@transition-energie-france.fr • Service gratuit - Réponse sous 24h
              </p>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-orange-50 p-6 rounded-lg border border-orange-100">
              <h4 className="font-semibold text-gray-900 mb-3">
                Zone d'intervention
              </h4>
              <div className="text-sm space-y-2">
                <p className="text-gray-700">
                  <strong>Actuellement disponible :</strong> Paris (75), Seine-et-Marne (77), 
                  Essonne (91), Hauts-de-Seine (92), Seine-Saint-Denis (93), Val-d'Oise (95).
                </p>
                <p className="text-gray-700">
                  <strong>Ouverture prévue :</strong> Yvelines (78), Val-de-Marne (94), Oise (60).
                </p>
              </div>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg border border-green-100">
              <h4 className="font-semibold text-gray-900 mb-3">
                Délais de traitement
              </h4>
              <p className="text-gray-700 text-sm">
                Simulation d'éligibilité : immédiate • Visite spécialiste : sous 72 heures • 
                Dépôt dossier ANAH : sous 1 semaine • Début travaux : 2-4 mois • 
                Versement aides : 2-4 semaines après fin travaux.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />
    </section>
  );
};

export default FAQ;